/**
 * Collection Registry
 * Maps each database collection to its metadata, security settings, and navigation group
 *
 * Nav Group -> Collections mapping (assumed fields + relationships):
 *
 * A. Platform Overview
 *   - userActivity: pk=id; fields { userId, action, route, createdAt } (assumed)
 *   - sessions: pk=id; fields { userId, ipAddress, userAgent, active } (assumed)
 *   - security_logs: pk=id; fields { userId, eventType, severity } (assumed) [Critical]
 *
 * B. Observability & Security
 *   - security_logs (Critical) -> users.id (assumed)
 *   - stageAuditLogs (High) -> users.id, program/product ids (assumed)
 *   - legal_consents (High) -> users.id, consentType, version (assumed)
 *   - otps, clientLoginOtps, passwordResetOtps (High) -> users.id/email (assumed)
 *
 * C. Users & Identity
 *   - users (High) -> organizations.id (assumed)
 *   - deleted_users (High) -> original user id (assumed)
 *   - sessions (High)
 *   - otps, clientLoginOtps, passwordResetOtps (High)
 *
 * D. Clients & Organizations
 *   - organizations (Medium)
 *   - clients (High) -> organizations.id (assumed)
 *   - clientEmployees (Medium) -> clients.id (assumed)
 *   - companyProfiles (Medium) -> organizations.id (assumed)
 *
 * E. Programs & Coaching
 *   - programs (Medium)
 *   - sessions (High) -> users, coaches, clients (assumed)
 *   - sessionNotes (High) -> sessions.id (assumed)
 *   - coachingLog (High) -> sessions.id, users.id (assumed)
 *   - tasks (Medium) -> users.id, programs.id (assumed)
 *   - programRatings (Medium) -> programs.id, users.id (assumed)
 *
 * F. Product Development & Audits
 *   - product_development (Medium)
 *   - productDevelopmentSubmissions (High) -> product_development.id (assumed)
 *   - productDevelopmentAuditChecklists (Medium) -> submissions.id (assumed)
 *   - productAudits (High) -> submissions.id (assumed)
 *
 * G. Content & Knowledge
 *   - knowledgeBase (Medium)
 *   - knowledgeBaseAnalytics (Medium) -> knowledgeBase.id (assumed)
 *   - bestPractices, bestPracticesAnalytics (Medium)
 *
 * H. Messaging & Collaboration
 *   - messages (High) -> threads.id, users.id (assumed)
 *   - threads (High) -> participant user ids (assumed)
 *   - private_chats (High)
 *
 * I. Templates & Document Bank
 *   - templates, templatesAnalytics (Medium)
 *   - documentBank, documentCategories, documentBankViews (Medium)
 *
 * J. Data Quality & Admin Tools
 *   - All above as needed
 */

export const NAV_GROUPS = {
  PLATFORM_OVERVIEW: 'platformOverview',
  OBSERVABILITY_SECURITY: 'observabilitySecurity',
  USERS_IDENTITY: 'usersIdentity',
  CLIENTS_ORGANIZATIONS: 'clientsOrganizations',
  PROGRAMS_COACHING: 'programsCoaching',
  PRODUCT_DEV_AUDITS: 'productDevAudits',
  CONTENT_KNOWLEDGE: 'contentKnowledge',
  MESSAGING_COLLABORATION: 'messagingCollaboration',
  TEMPLATES_DOCUMENTS: 'templatesDocuments',
  DATA_QUALITY_ADMIN: 'dataQualityAdmin',
  HTTP_REFERENCE: 'httpReference',
};

export const COLLECTION_REGISTRY = {
  users: {
    name: 'users',
    label: 'Users',
    navGroup: NAV_GROUPS.USERS_IDENTITY,
    sensitivity: 'High',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['email', 'displayName'],
    readOnlyFor: ['moderator'],
  },
  deleted_users: {
    name: 'deleted_users',
    label: 'Deleted Users',
    navGroup: NAV_GROUPS.USERS_IDENTITY,
    sensitivity: 'High',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'deletedAt', direction: 'desc' },
    searchableFields: ['email', 'displayName'],
  },
  sessions: {
    name: 'sessions',
    label: 'Sessions',
    navGroup: NAV_GROUPS.USERS_IDENTITY,
    sensitivity: 'High',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['userId', 'ipAddress'],
  },
  userActivity: {
    name: 'userActivity',
    label: 'User Activity',
    navGroup: NAV_GROUPS.PLATFORM_OVERVIEW,
    sensitivity: 'High',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['userId', 'action', 'route'],
  },
  security_logs: {
    name: 'security_logs',
    label: 'Security Logs',
    navGroup: NAV_GROUPS.OBSERVABILITY_SECURITY,
    sensitivity: 'Critical',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['eventType', 'userId'],
  },
  stageAuditLogs: {
    name: 'stageAuditLogs',
    label: 'Stage Audit Logs',
    navGroup: NAV_GROUPS.OBSERVABILITY_SECURITY,
    sensitivity: 'High',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['userId', 'eventType'],
  },
  legal_consents: {
    name: 'legal_consents',
    label: 'Legal Consents',
    navGroup: NAV_GROUPS.OBSERVABILITY_SECURITY,
    sensitivity: 'High',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['userId', 'consentType'],
  },
  otps: {
    name: 'otps',
    label: 'OTPs',
    navGroup: NAV_GROUPS.USERS_IDENTITY,
    sensitivity: 'High',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['email', 'userId'],
  },
  clientLoginOtps: {
    name: 'clientLoginOtps',
    label: 'Client Login OTPs',
    navGroup: NAV_GROUPS.USERS_IDENTITY,
    sensitivity: 'High',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['email', 'clientId'],
  },
  passwordResetOtps: {
    name: 'passwordResetOtps',
    label: 'Password Reset OTPs',
    navGroup: NAV_GROUPS.USERS_IDENTITY,
    sensitivity: 'High',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['email', 'userId'],
  },
  organizations: {
    name: 'organizations',
    label: 'Organizations',
    navGroup: NAV_GROUPS.CLIENTS_ORGANIZATIONS,
    sensitivity: 'Medium',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['name'],
  },
  clients: {
    name: 'clients',
    label: 'Clients',
    navGroup: NAV_GROUPS.CLIENTS_ORGANIZATIONS,
    sensitivity: 'High',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['email', 'name'],
  },
  clientEmployees: {
    name: 'clientEmployees',
    label: 'Client Employees',
    navGroup: NAV_GROUPS.CLIENTS_ORGANIZATIONS,
    sensitivity: 'Medium',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['name', 'email'],
  },
  companyProfiles: {
    name: 'companyProfiles',
    label: 'Company Profiles',
    navGroup: NAV_GROUPS.CLIENTS_ORGANIZATIONS,
    sensitivity: 'Medium',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['name'],
  },
  programs: {
    name: 'programs',
    label: 'Programs',
    navGroup: NAV_GROUPS.PROGRAMS_COACHING,
    sensitivity: 'Medium',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['name', 'title'],
  },
  sessionNotes: {
    name: 'sessionNotes',
    label: 'Session Notes',
    navGroup: NAV_GROUPS.PROGRAMS_COACHING,
    sensitivity: 'High',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['sessionId', 'note'],
  },
  coachingLog: {
    name: 'coachingLog',
    label: 'Coaching Log',
    navGroup: NAV_GROUPS.PROGRAMS_COACHING,
    sensitivity: 'High',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['userId', 'sessionId'],
  },
  tasks: {
    name: 'tasks',
    label: 'Tasks',
    navGroup: NAV_GROUPS.PROGRAMS_COACHING,
    sensitivity: 'Medium',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['title', 'description'],
  },
  programRatings: {
    name: 'programRatings',
    label: 'Program Ratings',
    navGroup: NAV_GROUPS.PROGRAMS_COACHING,
    sensitivity: 'Medium',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['programId', 'userId'],
  },
  product_development: {
    name: 'product_development',
    label: 'Product Development',
    navGroup: NAV_GROUPS.PRODUCT_DEV_AUDITS,
    sensitivity: 'Medium',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['name', 'title'],
  },
  productDevelopmentSubmissions: {
    name: 'productDevelopmentSubmissions',
    label: 'Product Development Submissions',
    navGroup: NAV_GROUPS.PRODUCT_DEV_AUDITS,
    sensitivity: 'High',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['companyId', 'status'],
  },
  productDevelopmentAuditChecklists: {
    name: 'productDevelopmentAuditChecklists',
    label: 'Product Dev Audit Checklists',
    navGroup: NAV_GROUPS.PRODUCT_DEV_AUDITS,
    sensitivity: 'Medium',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['submissionId'],
  },
  productAudits: {
    name: 'productAudits',
    label: 'Product Audits',
    navGroup: NAV_GROUPS.PRODUCT_DEV_AUDITS,
    sensitivity: 'High',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['submissionId'],
  },
  knowledgeBase: {
    name: 'knowledgeBase',
    label: 'Knowledge Base',
    navGroup: NAV_GROUPS.CONTENT_KNOWLEDGE,
    sensitivity: 'Medium',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['title', 'content'],
  },
  knowledgeBaseAnalytics: {
    name: 'knowledgeBaseAnalytics',
    label: 'Knowledge Base Analytics',
    navGroup: NAV_GROUPS.CONTENT_KNOWLEDGE,
    sensitivity: 'Medium',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['articleId'],
  },
  bestPractices: {
    name: 'bestPractices',
    label: 'Best Practices',
    navGroup: NAV_GROUPS.CONTENT_KNOWLEDGE,
    sensitivity: 'Medium',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['title', 'content'],
  },
  bestPracticesAnalytics: {
    name: 'bestPracticesAnalytics',
    label: 'Best Practices Analytics',
    navGroup: NAV_GROUPS.CONTENT_KNOWLEDGE,
    sensitivity: 'Medium',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['practiceId'],
  },
  messages: {
    name: 'messages',
    label: 'Messages',
    navGroup: NAV_GROUPS.MESSAGING_COLLABORATION,
    sensitivity: 'High',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['body', 'senderId'],
  },
  threads: {
    name: 'threads',
    label: 'Threads',
    navGroup: NAV_GROUPS.MESSAGING_COLLABORATION,
    sensitivity: 'High',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['participantIds'],
  },
  private_chats: {
    name: 'private_chats',
    label: 'Private Chats',
    navGroup: NAV_GROUPS.MESSAGING_COLLABORATION,
    sensitivity: 'High',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['participantIds'],
  },
  templates: {
    name: 'templates',
    label: 'Templates',
    navGroup: NAV_GROUPS.TEMPLATES_DOCUMENTS,
    sensitivity: 'Medium',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['name', 'title'],
  },
  templatesAnalytics: {
    name: 'templatesAnalytics',
    label: 'Templates Analytics',
    navGroup: NAV_GROUPS.TEMPLATES_DOCUMENTS,
    sensitivity: 'Medium',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['templateId'],
  },
  documentBank: {
    name: 'documentBank',
    label: 'Document Bank',
    navGroup: NAV_GROUPS.TEMPLATES_DOCUMENTS,
    sensitivity: 'Medium',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['title', 'name'],
  },
  documentCategories: {
    name: 'documentCategories',
    label: 'Document Categories',
    navGroup: NAV_GROUPS.TEMPLATES_DOCUMENTS,
    sensitivity: 'Medium',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['name'],
  },
  documentBankViews: {
    name: 'documentBankViews',
    label: 'Document Bank Views',
    navGroup: NAV_GROUPS.TEMPLATES_DOCUMENTS,
    sensitivity: 'Medium',
    allowedRoles: ['developer', 'admin', 's_admin'],
    defaultSort: { field: 'createdAt', direction: 'desc' },
    searchableFields: ['documentId', 'userId'],
  },
};

export const getCollectionByKey = key => COLLECTION_REGISTRY[key];
export const getCollectionsByNavGroup = navGroup =>
  Object.values(COLLECTION_REGISTRY).filter(c => c.navGroup === navGroup);
export const getAllCollectionKeys = () => Object.keys(COLLECTION_REGISTRY);
